import { styled } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { QCard, QCardContent, QTextField, QTypography, QButton } from '@kw/quokka-ui';
import { SettingsMode } from '../enum/SettingsMode.enum';
import { sendHttpRequest } from '../../../utils/network.service';
import { MutatorOptions } from 'swr';

import { validateEmail } from '../../../utils/emailValidation';
import { KwFormErrorMessage } from '../../../kw-ui-components/KwFormErrorMessage';
import { AuthPermissions } from '../../../auth/AuthPermissions';
import { verifyAuthPermission } from '../../../auth/VerifyAuthPermission';

interface CustomSupportEmailCardProps {
  isAdmin: boolean;
  currentSupportEmail: string;
  mutate: (data?: any, opts?: boolean | MutatorOptions<any>) => Promise<any>;
}

export const CustomSupportEmailCard = ({ isAdmin, currentSupportEmail, mutate }: CustomSupportEmailCardProps) => {
  const [mode, setMode] = useState(SettingsMode.READ);
  const isEditMode = mode === SettingsMode.EDIT;
  const [supportEmail, setSupportEmail] = useState(currentSupportEmail);
  const [emailInputError, setEmailInputError] = useState('');
  const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(true);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (supportEmail.length) {
      setIsSaveButtonDisabled(false);
    }
    if (supportEmail.length === 0) {
      setIsSaveButtonDisabled(true);
    }
  }, [supportEmail]);

  const resetInputValues = () => {
    setSupportEmail('');
    setMode(SettingsMode.READ);
    setEmailInputError('');
    mutate();
  };

  const updateSupportEmail = async () => {
    if (validateEmail(supportEmail)) {
      const body = { supportEmail };
      await sendHttpRequest({
        path: `${process.env.ORGANIZATION_SERVICE_URL}/organizations/`,
        method: 'PUT',
        body,
        successMessage: 'Successfully updated Q-Scout support email',
        errorMessage: 'Error updating Q-Scout support email',
        enqueueSnackbar,
      });
      resetInputValues();
    } else {
      setEmailInputError('Please enter a valid email address');
    }
  };

  if (!verifyAuthPermission(AuthPermissions.SETTINGS_SUPPORT_EMAIL)) return null;

  return (
    <StyledQCard>
      <QCardContent>
        <StyledQTypography variant="h4Bold">Support Email</StyledQTypography>
        <StyledQTypography variant="bodyRegular">
          Enter the email address where Q-Scout in-app support requests should be sent.
        </StyledQTypography>
        <StyledQTypography variant="h5Medium">Email</StyledQTypography>
        {isAdmin && isEditMode ? (
          <>
            <QTextField
              placeholder={supportEmail.length ? supportEmail : currentSupportEmail}
              onChange={e => {
                setSupportEmail(e.target.value);
              }}
              inputProps={{ 'aria-label': 'supportEmail' }}
            />
            {emailInputError && (
              <StyledErrorMessageWrapper>
                <KwFormErrorMessage key={emailInputError} error={Boolean(emailInputError)} reason={emailInputError} />
              </StyledErrorMessageWrapper>
            )}
            <StyledButtonWrapper>
              <QButton onClick={resetInputValues}>Cancel</QButton>
              <QButton variant="filled" disabled={isSaveButtonDisabled} onClick={updateSupportEmail}>
                Save
              </QButton>
            </StyledButtonWrapper>
          </>
        ) : (
          <>
            <StyledQTypography>{currentSupportEmail}</StyledQTypography>
            <StyledButtonWrapper>
              <QButton variant="filled" onClick={() => setMode(SettingsMode.EDIT)}>
                Edit
              </QButton>
            </StyledButtonWrapper>
          </>
        )}
      </QCardContent>
    </StyledQCard>
  );
};

const StyledQCard = styled(QCard)`
  width: 100%;
  min-width: 300px;
  max-width: 400px;
`;

const StyledQTypography = styled(QTypography)`
  margin: 20px 0;
  display: block;
`;

const StyledButtonWrapper = styled('div')`
  display: flex;
  justify-content: flex-end;
  column-gap: 14px;
  margin-top: 24px;
`;

const StyledErrorMessageWrapper = styled('div')`
  margin: 14px 0;
`;

const StyledCardContainer = styled('div')`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  width: 100%;
`;
